// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what a room's lead shows besides the datasets themselves (owner,
 * 2026-10-22, meeting test 4: the palette, the colour range, the data pins
 * and the pin graph did not follow the lead): its colours and its data
 * pins, turned into the wire's compact forms (`RoomState.colours`,
 * `RoomState.dataPins`) and back.
 *
 * Pure, so `roomSync` stays free of the app's colour and pin types and the
 * rules are tested here: absent fields are the identity, a following pin's
 * place is not a change (each device finds the extreme in its own frame),
 * and a state that would not fit one message loses the least important
 * part first.
 */

import { DEFAULT_DISPLAY, type ColorScaleDisplay } from './colorScaleDisplay'
import { MEASUREMENT_COLORS } from './measure/colors'
import type { Pin, PinTag } from './measure/pins'
import type { SurfaceMode } from './measure/surfaceMask'
import { ROOM_MAX_COLOURS, ROOM_MAX_DATA_PINS, ROOM_MAX_MESSAGE_CHARS, dataPinPoint, type RoomColours, type RoomDataPin, type RoomDataPins, type RoomState } from './roomProtocol'

/** A colour display in the wire's form: only what differs from the identity. */
export function roomColoursOf(id: string, display: ColorScaleDisplay): RoomColours {
  const out: RoomColours = { id, palette: display.palette }
  if (display.inverted) out.inverted = true
  const { lo, hi } = display.stretch
  if ((lo !== 0 || hi !== 1) && lo >= 0 && hi <= 1 && lo < hi) out.stretch = [lo, hi]
  const l = display.limits
  if (l && Number.isFinite(l.min) && Number.isFinite(l.max) && l.min < l.max && (l.scale !== 'log' || l.min > 0)) {
    out.limits = [l.min, l.max, l.scale]
  }
  const { min, max } = display.threshold
  if (min !== null || max !== null) out.threshold = [min, max]
  return out
}

/** The display a room's colours stand for. */
export function displayOfRoomColours(c: RoomColours): ColorScaleDisplay {
  return {
    palette: c.palette,
    inverted: !!c.inverted,
    stretch: c.stretch ? { lo: c.stretch[0], hi: c.stretch[1] } : { ...DEFAULT_DISPLAY.stretch },
    limits: c.limits ? { min: c.limits[0], max: c.limits[1], scale: c.limits[2] } : null,
    threshold: c.threshold ? { min: c.threshold[0], max: c.threshold[1] } : { min: null, max: null },
  }
}

/** Whether two displays draw the same colours (what the wire would say of each). */
export function sameColours(a: ColorScaleDisplay, b: ColorScaleDisplay): boolean {
  return JSON.stringify(roomColoursOf('', a)) === JSON.stringify(roomColoursOf('', b))
}

/** A pin's colour as the wire's index; -1 for one outside the palette. */
export function pinColorIndex(color: string): number {
  return (MEASUREMENT_COLORS as readonly string[]).indexOf(color)
}

/** What the store's `adopt` takes: a lead's pin in the app's terms. */
export interface AdoptedPin {
  lat: number
  lon: number
  color: string
  name: string | null
  tag: PinTag | null
  follow: boolean
}

/** The lead's pins and graph, for the wire; undefined when it has neither. */
export function roomDataPinsOf(
  pins: readonly Pin[],
  opts: { graph: boolean; track: boolean; surface: SurfaceMode },
): RoomDataPins | undefined {
  const list: RoomDataPin[] = []
  for (const pin of pins.slice(0, ROOM_MAX_DATA_PINS)) {
    const color = pinColorIndex(pin.color)
    const at = dataPinPoint(pin.lat, pin.lon)
    if (color < 0 || !at || list.some((p) => p.color === color)) continue
    const out: RoomDataPin = { lat: at[0], lon: at[1], color }
    if (pin.name) out.name = pin.name
    if (pin.tag) {
      out.tag = pin.tag
      if (pin.follow) out.follow = true
    }
    list.push(out)
  }
  if (!list.length && !opts.graph) return undefined
  return {
    pins: list,
    ...(opts.graph ? { graph: true as const } : {}),
    ...(opts.track ? { track: true as const } : {}),
    ...(opts.surface !== 'all' ? { surface: opts.surface } : {}),
  }
}

/** The lead's pins in the app's terms, for `PinStore.adopt`. */
export function adoptedPinsOf(data: RoomDataPins | null | undefined): AdoptedPin[] {
  return (data?.pins ?? []).map((p) => ({
    lat: p.lat,
    lon: p.lon,
    color: MEASUREMENT_COLORS[p.color] ?? MEASUREMENT_COLORS[0],
    name: p.name ?? null,
    tag: p.tag ?? null,
    follow: !!p.follow,
  }))
}

/**
 * What is worth sending of the pins: a following pin's place changes with
 * every frame, and each follower finds the extreme in its own, so it is
 * left out (the heartbeat still carries it once a second, for a follower
 * that cannot read the values itself).
 */
export function dataPinsKey(data: RoomDataPins | null | undefined): string {
  if (!data) return 'null'
  return JSON.stringify({ ...data, pins: data.pins.map((p) => (p.follow ? { ...p, lat: 0, lon: 0 } : p)) })
}

/**
 * The colours and pins cut until the state carrying them fits one message
 * (`ROOM_MAX_MESSAGE_CHARS`), leaving room for `reserve` characters more
 * (a Khipu note is cut after these, by `fitNote`). Colours go from the end
 * first (the overlays' and the other globes' after the main dataset's),
 * then the pins' names, then the pins from the end; the main dataset's
 * colours go last.
 */
export function fitLook(
  base: Omit<RoomState, 'note' | 'colours' | 'dataPins'>,
  colours: readonly RoomColours[],
  dataPins: RoomDataPins | undefined,
  reserve = 0,
): Pick<RoomState, 'colours' | 'dataPins'> {
  // The envelope ({"t":"state","s":…}) and some slack for escaping, as fitNote leaves.
  const budget = ROOM_MAX_MESSAGE_CHARS - 64 - reserve
  let c = colours.slice(0, ROOM_MAX_COLOURS)
  let d: RoomDataPins | undefined = dataPins ? { ...dataPins, pins: [...dataPins.pins] } : undefined
  const parts = (): Pick<RoomState, 'colours' | 'dataPins'> => ({ ...(c.length ? { colours: c } : {}), ...(d ? { dataPins: d } : {}) })
  const size = (): number => JSON.stringify({ ...base, ...parts() }).length
  while (size() > budget && c.length > 1) c = c.slice(0, -1)
  if (d && size() > budget) d = { ...d, pins: d.pins.map(({ name: _name, ...p }) => p) }
  while (d && size() > budget && d.pins.length) d = { ...d, pins: d.pins.slice(0, -1) }
  if (d && !d.pins.length && !d.graph) d = undefined
  if (size() > budget) c = []
  if (size() > budget) d = undefined
  return parts()
}
